import { FC, useEffect, useMemo, useState } from "react";
import { Modal, Row, Col, Tag, Divider, Input, Button, Space } from "antd";
import { useSelector } from "react-redux";
import { RootState } from "../../../store/store";
import { resolveHeritageHealthIndiaClaimId } from "../../../utils/heritageHealthIndiaClaimId";
import { resolveClaimResponseDto } from "../../../utils/portalClaimValidation";

export type PortalClaimValidationScheme =
  | "HEALTH_INDIA"
  | "HERITAGE"
  | "FHPL"
  | "STAR_HEALTH";

type FieldConfig = {
  label: string;
  key: string;
};

const SCHEME_FIELDS: Record<PortalClaimValidationScheme, FieldConfig[]> = {
  HEALTH_INDIA: [
    { label: "Claim ID", key: "claimId" },
    { label: "Patient Name", key: "patientName" },
  ],
  HERITAGE: [
    { label: "Claim ID", key: "claimId" },
    { label: "Patient Name", key: "patientName" },
  ],
  FHPL: [
    { label: "Claim ID", key: "claimId" },
    { label: "Patient Name", key: "patientName" },
  ],
  STAR_HEALTH: [
    { label: "Claim ID", key: "claimId" },
    { label: "OP/IP Number", key: "opNumber" },
    { label: "Patient Name", key: "patientName" },
  ],
};

type PortalClaimValidationModalProps = {
  open: boolean;
  onCancel: () => void;
  validationRes: any;
  scheme: PortalClaimValidationScheme;
  onUpdate?: (updatedG7: any) => void | Promise<void>;
};

const PortalClaimValidationModal: FC<PortalClaimValidationModalProps> = ({
  open,
  onCancel,
  validationRes,
  scheme,
  onUpdate,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [isUpdateLoading, setIsUpdateLoading] = useState(false);
  const { patients, preAuth } = useSelector((state: RootState) => state);

  const resolveInitialG7Data = () => {
    const healthIndiaData =
      preAuth?.portalData?.casePreAuthOthersResponse?.data ||
      preAuth?.portalData?.casePreAuthHealthIndia?.data;
    const heritageData =
      preAuth?.portalData?.casePreAuthHeritage?.data ||
      preAuth?.portalData?.casePreAuthOthersResponse?.data;
    const fhplData =
      preAuth?.portalData?.casePreAuthFhpl?.data ||
      preAuth?.portalData?.casePreAuthOthersResponse?.data;

    const claimId =
      scheme === "STAR_HEALTH"
        ? preAuth?.portalData?.schemeRegistrationId ||
          preAuth?.portalData?.casePreAuthStarhealth?.claimId
        : scheme === "HERITAGE"
          ? heritageData?.claimId || resolveHeritageHealthIndiaClaimId(preAuth?.portalData)
          : scheme === "FHPL"
            ? fhplData?.claimId || resolveHeritageHealthIndiaClaimId(preAuth?.portalData)
            : healthIndiaData?.claimId ||
              resolveHeritageHealthIndiaClaimId(preAuth?.portalData);

    return {
      patientName: patients?.fetchBasicDetails?.patientResponse?.fullName,
      claimId,
      opNumber: patients?.fetchBasicDetails?.visitInfoResponse?.opNumber,
    };
  };

  const [g7Data, setG7Data] = useState<any>(() => resolveInitialG7Data());

  useEffect(() => {
    if (open) {
      setG7Data(resolveInitialG7Data());
      setIsEditing(false);
      setIsUpdateLoading(false);
    }
  }, [open, preAuth?.portalData, patients?.fetchBasicDetails]);

  const fields = SCHEME_FIELDS[scheme];
  const claimResponse = resolveClaimResponseDto(validationRes);

  const handleChange = (field: string, value: any) => {
    setG7Data({ ...g7Data, [field]: value });
  };

  const handleUpdate = async () => {
    if (!onUpdate) {
      return;
    }

    try {
      setIsUpdateLoading(true);
      await onUpdate(g7Data);
      setIsEditing(false);
    } finally {
      setIsUpdateLoading(false);
    }
  };

  const hasMismatch = useMemo(() => {
    return fields.some(({ key }) => {
      const portalValue = claimResponse?.[key];
      const g7Value = g7Data?.[key];
      return (
        portalValue?.toString()?.toLowerCase()?.trim() !==
        g7Value?.toString()?.toLowerCase()?.trim()
      );
    });
  }, [claimResponse, g7Data, fields]);

  const derivedStatus = isEditing
    ? "DISCREPANCY"
    : hasMismatch
      ? "DISCREPANCY"
      : validationRes?.status;

  const renderFieldRow = (label: string, fieldKey: string) => {
    const portalValue = claimResponse?.[fieldKey];
    const g7Value = g7Data?.[fieldKey];
    const isMismatch =
      portalValue?.toString()?.toLowerCase()?.trim() !==
      g7Value?.toString()?.toLowerCase()?.trim();

    return (
      <Row
        key={fieldKey}
        gutter={16}
        style={{
          background: "#fff",
          padding: "8px 12px",
          borderRadius: 8,
          marginBottom: 10,
          boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
        }}
      >
        <Col span={8}>
          <strong>{label}</strong>
        </Col>
        <Col span={8}>
          <span>{portalValue || "-"}</span>
        </Col>
        <Col
          span={8}
          style={{
            color: isMismatch ? "#d92955ff" : "inherit",
            borderRadius: 6,
            padding: isEditing ? 0 : "6px 0",
          }}
        >
          {isEditing ? (
            <Input
              value={g7Value}
              onChange={(e) => handleChange(fieldKey, e.target.value)}
            />
          ) : (
            <span>{g7Value || "-"}</span>
          )}
        </Col>
      </Row>
    );
  };

  return (
    <Modal
      title={
        <div style={{ fontSize: 18, fontWeight: 600 }}>
          Claim Validation
        </div>
      }
      open={open}
      footer={null}
      onCancel={onCancel}
      width={900}
      bodyStyle={{ background: "#fafafa", padding: "20px 25px" }}
    >
      <Row style={{ marginBottom: 10 }}>
        <Col span={8}>
          <strong style={{ color: "rgb(0, 76, 143)" }}>Field</strong>
        </Col>
        <Col span={8}>
          <strong style={{ color: "rgb(0, 76, 143)" }}>Portal Details</strong>
        </Col>
        <Col span={8}>
          <strong style={{ color: "rgb(0, 76, 143)" }}>G7 Pro Details</strong>
        </Col>
      </Row>

      {fields.map(({ label, key }) => renderFieldRow(label, key))}

      <Divider />

      <div style={{ textAlign: "center", margin: "20px 0" }}>
        <strong>Status: </strong>
        {derivedStatus === "VALIDATED" ? (
          <Tag color="success" style={{ fontSize: 15, padding: "6px 15px" }}>
            VALIDATED
          </Tag>
        ) : derivedStatus === "DISCREPANCY" ? (
          <Tag color="error" style={{ fontSize: 15, padding: "6px 15px" }}>
            MISMATCH
          </Tag>
        ) : (
          <Tag color="error" style={{ fontSize: 15, padding: "6px 15px" }}>
            NOT VALIDATED
          </Tag>
        )}
      </div>

      {!isEditing && validationRes?.status === "VALIDATED" && !hasMismatch ? (
        <></>
      ) : (
        <div style={{ textAlign: "center" }}>
          <Space>
            {!isEditing ? (
              <Button
                type="primary"
                style={{
                  backgroundColor: "rgb(0, 76, 143)",
                  color: "#fff",
                  boxShadow: "0px 2px 8px 0px #00000040",
                }}
                onClick={() => setIsEditing(true)}
              >
                Edit G7 Details
              </Button>
            ) : (
              <>
                <Button
                  type="primary"
                  loading={isUpdateLoading}
                  disabled={isUpdateLoading}
                  style={{
                    backgroundColor: "rgb(0, 76, 143)",
                    color: "#fff",
                    boxShadow: "0px 2px 8px 0px #00000040",
                  }}
                  onClick={handleUpdate}
                >
                  Update
                </Button>
                <Button
                  disabled={isUpdateLoading}
                  onClick={() => setIsEditing(false)}
                >
                  Cancel
                </Button>
              </>
            )}
          </Space>
        </div>
      )}
    </Modal>
  );
};

export default PortalClaimValidationModal;
